// docs/113 QP7: the walk into a stop on a quest's path. When the lead sends the band to a stop, the
// module opens its encounter at once; the path screen holds the route (`RouteDriver`) until the
// leader has walked to the node, the map has zoomed into it and faded to black, then releases the
// encounter. The black stays up while the encounter screen comes in and then fades away
// (`PathCurtain`). A path screen that is up says so (`pathInstance`) before any encounter opens,
// because the route's effects run before the screen's.
import { create } from 'zustand';
import { deviceNow } from '../time';

export type CurtainPhase = 'idle' | 'closing' | 'black' | 'opening';

/** The zoom and fade to black (ms); the black's hold once the encounter is up; the fade back. */
export const CURTAIN_CLOSE_MS = 1100;
export const CURTAIN_HOLD_MS = 450;
export const CURTAIN_OPEN_MS = 800;
/** The longest the route waits on a walk before it goes into the encounter anyway. */
export const PATH_HOLD_MAX_MS = 9000;

interface PathCurtainState {
  /** The instance whose path screen is up (the route holds its encounters for the walk). */
  pathInstance?: string;
  /** Encounters the walk has let go of (the route may enter them). */
  released: string[];
  phase: CurtainPhase;
  phaseAt: number;
  setPathInstance: (id: bigint | undefined) => void;
  /** The leader reached the node: zoom and fade to black. */
  close: () => void;
  /** The black is up: the route may enter the encounter. */
  release: (encounterId: bigint) => void;
  open: () => void;
  done: () => void;
}

export const usePathCurtain = create<PathCurtainState>((set) => ({
  released: [],
  phase: 'idle',
  phaseAt: 0,
  setPathInstance: (id) => set({ pathInstance: id?.toString() }),
  close: () => set({ phase: 'closing', phaseAt: deviceNow() }),
  release: (id) => set((s) => ({ released: s.released.includes(id.toString()) ? s.released : [...s.released.slice(-20), id.toString()], phase: s.phase === 'idle' ? 'idle' : 'black', phaseAt: deviceNow() })),
  open: () => set({ phase: 'opening', phaseAt: deviceNow() }),
  done: () => set({ phase: 'idle', phaseAt: deviceNow() }),
}));

/** Whether the route should wait before entering `encounterId` from the path screen of `instanceId`. */
export function pathHolds(s: Pick<PathCurtainState, 'pathInstance' | 'released'>, instanceId: bigint | undefined, encounterId: bigint): boolean {
  return instanceId !== undefined && s.pathInstance === instanceId.toString() && !s.released.includes(encounterId.toString());
}
